import { create } from 'zustand';
import type { Planet, PlayerRace } from '@kaeliux/shared';
import { getPlanet, listPlanets, createInitialPlanet, tickPlanet } from '../services/planet.service';
import { useUniverseStore } from './universeStore';

const CURRENT_PLANET_KEY = 'kaeliux-current-planet';

// Counts overlapping in-flight tick requests so `isTicking` only clears once
// the LAST one finishes — a slower earlier tick completing after a newer one
// must not flip the indicator off while the newer one is still in flight.
let tickingCount = 0;

function getStoredPlanetId() {
    return localStorage.getItem(CURRENT_PLANET_KEY);
}

function setStoredPlanetId(planetId: string) {
    localStorage.setItem(CURRENT_PLANET_KEY, planetId);
}

function clearStoredPlanetId() {
    localStorage.removeItem(CURRENT_PLANET_KEY);
}

interface PlanetStoreState {
    currentPlanet: Planet | null;
    /** Every planet the player owns — the source list for the top-nav switcher. */
    planets: Planet[];
    /**
     * The planet the player is currently viewing/managing. Client-only
     * (persisted in localStorage), distinct from the profile's `mainPlanetId`
     * homeworld — switching planets is a view change, not a home change.
     */
    activePlanetId: string | null;
    isLoading: boolean;
    /** A `tickCurrentPlanet` request is in flight — drives the pages' tick spinner. */
    isTicking: boolean;
    error: string | null;
    loadPlanet: (userId: string, planetId?: string) => Promise<void>;
    loadPlanets: (userId: string) => Promise<void>;
    setActivePlanet: (planetId: string, userId: string) => Promise<void>;
    createInitialPlanet: (userId: string, race: PlayerRace) => Promise<string>;
    /**
     * Apply elapsed time to `currentPlanet` server-side and store the result.
     * No-ops when no planet is loaded, so callers (the game loop, page
     * actions) never have to null-check first.
     */
    tickCurrentPlanet: () => Promise<void>;
    /**
     * Adopt a planet snapshot some other call already fetched or ticked.
     *
     * Several endpoints tick the planet as a side effect of doing their own
     * job (`getPlayerState` resolves due research, `getShipUpgradeState`
     * resolves due upgrades) and hand back the result; ignoring it leaves the
     * UI rendering pre-tick resources until the next heartbeat. Ignores
     * snapshots for a planet the player is no longer viewing, and older ones
     * for the planet they are.
     */
    applyPlanet: (planet: Planet) => void;
}

export const usePlanetStore = create<PlanetStoreState>((set, get) => ({
    currentPlanet: null,
    planets: [],
    activePlanetId: getStoredPlanetId(),
    isLoading: false,
    isTicking: false,
    error: null,

    loadPlanet: async (userId: string, planetId?: string) => {
        set({ isLoading: true, error: null });

        const resolvedPlanetId = planetId || getStoredPlanetId();
        if (!resolvedPlanetId) {
            set({ currentPlanet: null, isLoading: false });
            return;
        }

        try {
            const planet = await getPlanet(userId, resolvedPlanetId);
            // Multiple callers load the same planet independently (e.g.
            // AppShell's own fetch alongside whatever the routed page
            // does) and can resolve out of order. A slower call landing
            // after a fresher one — e.g. one that includes a tick's updated
            // resources/ozone — must not clobber it; only apply this result
            // if it isn't older than what's already in the store.
            const existing = get().currentPlanet;
            const isStale = !!planet && !!existing && existing.id === planet.id
                && new Date(existing.updatedAt).getTime() > new Date(planet.updatedAt).getTime();
            set({ currentPlanet: isStale ? existing : planet, isLoading: false });
        } catch (error: unknown) {
            set({ error: (error as Error)?.message, isLoading: false, currentPlanet: null });
        }
    },

    loadPlanets: async (userId: string) => {
        try {
            const planets = await listPlanets(userId);
            // A previously-persisted `activePlanetId` (localStorage survives
            // across sessions/deploys indefinitely) can point at a planet
            // that no longer belongs to this player — self-heal by dropping
            // it rather than letting every downstream call keep targeting a
            // dead id, so the app falls back to the profile's mainPlanetId.
            const { activePlanetId } = get();
            if (activePlanetId && !planets.some((p) => p.id === activePlanetId)) {
                clearStoredPlanetId();
                set({ planets, activePlanetId: null });
                return;
            }
            set({ planets });
        } catch (error: unknown) {
            console.error('Failed to load planets:', error);
        }
    },

    /**
     * Switch which planet the player is viewing/managing. Persists the choice
     * locally and loads that planet as `currentPlanet`. Deliberately does NOT
     * change the profile's `mainPlanetId` — the homeworld (and its ranking
     * coordinates) stays put.
     */
    setActivePlanet: async (planetId: string, userId: string) => {
        setStoredPlanetId(planetId);
        set({ activePlanetId: planetId });
        await get().loadPlanet(userId, planetId);
    },

    createInitialPlanet: async (userId: string, race: PlayerRace) => {
        const planetId = await createInitialPlanet(userId, race);
        setStoredPlanetId(planetId);
        const planet = await getPlanet(userId, planetId);
        set({ currentPlanet: planet, activePlanetId: planetId });
        return planetId;
    },

    applyPlanet: (planet: Planet) => {
        const existing = get().currentPlanet;
        if (existing) {
            if (existing.id !== planet.id) return;
            if (new Date(existing.updatedAt).getTime() > new Date(planet.updatedAt).getTime()) return;
        }
        set({ currentPlanet: planet });
    },

    tickCurrentPlanet: async () => {
        const planetId = get().currentPlanet?.id;
        if (!planetId) return;

        const { resourceMultiplier, sunAges } = useUniverseStore.getState();
        // A fresher snapshot (e.g. a completed building-upgrade response)
        // landing while this tick is in flight must not be clobbered by the
        // tick's now-stale result — that's what would flash the pre-upgrade
        // energy value back and let a fast second click spend energy the
        // player no longer has. But several call sites (the game loop's
        // heartbeat, `setActivePlanet` on a planet switch) independently
        // re-fetch the SAME planet this tick already targets, and a plain
        // re-fetch landing mid-flight is not actually newer — it carries the
        // same `updatedAt` `loadPlanet`'s own staleness guard already keys
        // off. Comparing `updatedAt` (rather than "did the store change at
        // all") tells the two apart: a genuinely newer update discards this
        // tick; a same-vintage duplicate fetch does not get to discard our
        // fresher, ticked result just because it happened to land first.
        const startedFrom = get().currentPlanet;
        const startedAtUpdatedAt = startedFrom ? new Date(startedFrom.updatedAt).getTime() : 0;
        tickingCount++;
        set({ isTicking: true });
        try {
            const updated = await tickPlanet(planetId, { resourceMultiplier, sunAges });

            const latest = get().currentPlanet;
            const isStale = !latest
                || latest.id !== planetId
                || new Date(latest.updatedAt).getTime() > startedAtUpdatedAt;
            if (isStale) return;

            set({ currentPlanet: updated });
        } finally {
            tickingCount--;
            if (tickingCount === 0) set({ isTicking: false });
        }
    },
}));
